import { getOidcConfig } from "@/config/auth";
import type { OidcTokenInfo } from "@/lib/authSession";
import {
  invalidateRefreshTokenSession,
  recordOidcRefreshSuccess,
} from "@/lib/authSession";
import type {
  OidcRefreshFailureReason,
  RefreshOidcAccessTokenResult,
} from "@/lib/authSessionErrors";

let refreshPromise: Promise<RefreshOidcAccessTokenResult> | null = null;

const calculateExpiresAt = (expiresIn?: unknown) => {
  if (typeof expiresIn === "number") {
    return Date.now() + expiresIn * 1000;
  }

  if (typeof expiresIn === "string") {
    const parsed = Number.parseInt(expiresIn, 10);
    if (!Number.isNaN(parsed)) {
      return Date.now() + parsed * 1000;
    }
  }

  return undefined;
};

const readOauthError = (data: unknown): string | undefined => {
  if (
    typeof data === "object" &&
    data !== null &&
    "error" in data &&
    typeof data.error === "string"
  ) {
    return data.error;
  }

  return undefined;
};

const isMissingRefreshTokenError = (data: unknown): boolean => {
  if (
    typeof data !== "object" ||
    data === null ||
    !("error" in data) ||
    data.error !== "invalid_request" ||
    !("error_description" in data) ||
    typeof data.error_description !== "string"
  ) {
    return false;
  }

  const description = data.error_description.toLowerCase();
  return /(?:missing\s+(?:the\s+)?(?:required\s+)?(?:parameter\s*:\s*)?["']?refresh_token|["']?refresh_token["']?(?:\s+parameter)?\s+(?:is\s+)?(?:missing|required)|必須パラメータが不足しています[:：]\s*["']?refresh_token)\b/.test(
    description,
  );
};

export const classifyOidcRefreshResponse = async (
  response: Response,
): Promise<RefreshOidcAccessTokenResult> => {
  let data: unknown = {};

  try {
    data = await response.json();
  } catch {
    // Non-JSON error responses still receive an HTTP classification.
  }

  if (response.ok) {
    const responseData = typeof data === "object" && data !== null ? data : {};
    const accessToken =
      "access_token" in responseData ? responseData.access_token : undefined;

    if (typeof accessToken !== "string" || accessToken.trim() === "") {
      return { tokenInfo: null, status: "protocol_error" };
    }

    const tokenInfo: OidcTokenInfo = {
      token: accessToken,
      expiresAt: calculateExpiresAt(
        "expires_in" in responseData ? responseData.expires_in : undefined,
      ),
    };

    return { tokenInfo, status: "success" };
  }

  const oauthError = readOauthError(data);
  let status: Exclude<OidcRefreshFailureReason, "network_error">;

  if (response.status === 429) {
    status = "rate_limited";
  } else if (
    oauthError === "invalid_grant" ||
    (response.status === 400 && isMissingRefreshTokenError(data))
  ) {
    status = "invalid_refresh_token";
    invalidateRefreshTokenSession();
  } else if (response.status === 400 || response.status === 401) {
    status = "protocol_error";
  } else {
    status = "http_error";
  }

  return {
    tokenInfo: null,
    status,
    ...(oauthError ? { oauthError } : {}),
  };
};

export const refreshOidcAccessToken =
  async (): Promise<RefreshOidcAccessTokenResult> => {
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        const oidcConfig = getOidcConfig();
        const tokenEndpoint =
          oidcConfig.metadata?.token_endpoint ||
          `${import.meta.env.VITE_OIDC_AUTHORITY}/oauth/token`;

        const formData = new URLSearchParams();
        formData.append("grant_type", "refresh_token");
        formData.append("client_id", import.meta.env.VITE_OIDC_CLIENT_ID);

        const response = await fetch(tokenEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData,
          credentials: "include",
        });

        const result = await classifyOidcRefreshResponse(response);
        if (result.status === "success") {
          const { tokenInfo } = result;
          recordOidcRefreshSuccess(tokenInfo);
          return result;
        }

        return result;
      } catch {
        return { tokenInfo: null, status: "network_error" };
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };
